import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TAddExistingResourcesFormProps } from '@/console/features/groups/components/add-existing-resources/add-existing-resources-form.types';
import {
  TAddExistingResource,
  TAddExistingResourcesInputSchema,
} from '@/console/features/groups/components/add-existing-resources/add-existing-resources.types';
import { withTypename } from '@/console/features/groups/components/add-existing-resources/add-existing-resources.utils';
import { useGroupedResourceElements } from '@/console/features/groups/hooks/use-grouped-resources-elements/use-grouped-resources-elements';
import { Alert } from '@/shared/components/ui/alert/alert';
import { Submit } from '@/shared/components/ui/form/components/submit/submit';
import { FormContent } from '@/shared/components/ui/form/form-content';
import { FormContainer } from '@/shared/components/ui/form/form.styled';
import { SelectedItemsList } from '@/shared/components/ui/selected-items-list/selected-items-list';
import { getResourceName } from '@/shared/features/groups/components/add-resources-to-group/add-resources-to-group-form.utils';

export const AddExistingResourcesForm: React.FC<
  TAddExistingResourcesFormProps
> = ({ typename, groupId, onClose, selectedItemsTitle, getItemId, select }) => {
  const { t } = useTranslation();
  const { getIcon } = useGroupedResourceElements();
  const { control, setValue, trigger } =
    useFormContext<TAddExistingResourcesInputSchema>();
  const resources = useWatch({
    control,
    name: 'resources',
    defaultValue: [],
  });

  const isInAnotherGroup = (resource: TAddExistingResource) =>
    Boolean(resource.groupId) && resource.groupId !== groupId;

  const movedCount = resources.filter(isInAnotherGroup).length;

  const getSelectedItems = () =>
    resources.map((resource) => ({
      icon: getIcon(typename),
      name: getResourceName(
        withTypename(resource, typename),
        t('common:unknown'),
      ),
      variant: isInAnotherGroup(resource) ? ('warning' as const) : undefined,
      onDelete: () => {
        setValue(
          'resources',
          resources.filter(
            (selected) => getItemId(selected) !== getItemId(resource),
          ),
        );
        void trigger('resources');
      },
    }));

  return (
    <FormContainer justifyContent="space-between">
      <FormContent>
        {select}
        {movedCount > 0 && (
          <Alert
            color="yellow"
            text={t('groups:resources_in_other_group', { count: movedCount })}
          />
        )}
        <SelectedItemsList
          title={selectedItemsTitle}
          selectedItems={getSelectedItems()}
        />
      </FormContent>
      <Submit onClose={onClose} submitButtonLabel={t('common:add')} />
    </FormContainer>
  );
};
